// community-library handles all consent-based suppression internally.
// Suppression is purely a function of OneTrust C0002 (Performance) consent state — no
// FeatureGate wiring needed in the consumer.
import { UIEventPayload } from '@atlassiansox/analytics-web-client';
import { Attributes } from '@atlassiansox/analytics-web-client/dist/types/types';
import { CommunityAnalyticsClient, eventType } from '@atlassian/community-library';

let communityClientInstance: typeof CommunityAnalyticsClient | null = null;
let initializationPromise: Promise<typeof CommunityAnalyticsClient | null> | null = null;

// Maximum time we will wait for OneTrust to publish any signal before
// falling through. If OneTrust is blocked or never loads, community-library will
// treat the missing signal as "opted out of performance cookies" and fire only
// the strictly-necessary `requested_page` operational event.
const ONE_TRUST_MAX_WAIT_MS = 3000;
const ONE_TRUST_POLL_INTERVAL_MS = 250;

type OneTrustWindow = typeof window & {
  OnetrustActiveGroups?: string;
};

const checkIfOneTrustIsLoaded = (): boolean => {
  const typedWindow = window as OneTrustWindow;
  return typeof typedWindow.OnetrustActiveGroups === 'string';
};

const waitForOneTrustToLoad = async (): Promise<void> =>
  new Promise((resolve) => {
    if (checkIfOneTrustIsLoaded()) {
      resolve();
      return;
    }
    const timers: { interval?: ReturnType<typeof setInterval>; timeout?: ReturnType<typeof setTimeout> } = {};
    timers.interval = setInterval((): void => {
      if (checkIfOneTrustIsLoaded()) {
        clearInterval(timers.interval);
        clearTimeout(timers.timeout);
        resolve();
      }
    }, ONE_TRUST_POLL_INTERVAL_MS);
    timers.timeout = setTimeout((): void => {
      clearInterval(timers.interval);
      resolve();
    }, ONE_TRUST_MAX_WAIT_MS);
  });

export const getCommunityAnalyticsClient = async (
  userId?: string,
): Promise<typeof CommunityAnalyticsClient | null> => {
  if (communityClientInstance) {
    return communityClientInstance;
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    await waitForOneTrustToLoad();

    const communityLibrary = await import('@atlassian/community-library');
    const { CommunityAnalyticsClient } = communityLibrary;

    communityClientInstance = CommunityAnalyticsClient;

    // Pass `null` for anonymous sessions so community-library skips the
    // legacy identity-cookie hydration and defensively clears any stale AAID on the
    // underlying analytics-web-client instance. Authenticated sessions pass the AAID
    // and the library wires setUserInfo as usual.
    CommunityAnalyticsClient.initWithPageAnalytics(
      CommunityAnalyticsClient.SUB_APP.HOME,
      {},
      null,
      undefined,
      userId ?? null,
    );

    return communityClientInstance;
  })();

  return initializationPromise;
};

export const sendScreenEvent = async (name: string, attributes: Attributes, userId?: string): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.SCREEN, {
    name,
    attributes,
  });
};

export const sendOperationalEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  attributes: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.OPERATIONAL, {
    source,
    action,
    actionSubject,
    attributes,
  });
};

export const sendTrackEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  attributes: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.TRACK, {
    source,
    action,
    actionSubject,
    attributes,
  });
};

export const sendUIEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  actionSubjectId?: string,
  attributes?: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.UI, {
    source,
    action,
    actionSubject,
    actionSubjectId,
    attributes,
  });
};

export const sendMarketingUIEvent = async (payload: UIEventPayload, userId?: string): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);
  const extendedPayload = {
    ...payload,
    attributes: {
      ...payload.attributes,
    },
  };

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.UI, extendedPayload);
};
